import { useCallback, useState } from "react";

export interface UsePagedStateOptions {
  /** Initial page (1-based). Defaults to 1. */
  initialPage?: number;
  /** Initial page size. Defaults to 20 (matches the carrier API default). */
  initialPageSize?: number;
  /** Initial search string. Defaults to "". */
  initialSearch?: string;
}

export interface PagedState {
  page: number;
  pageSize: number;
  search: string;
  /** Set the page directly (1-based). */
  setPage: (page: number) => void;
  /**
   * Set the page size. Resets the page back to 1 — a different page size
   * invalidates the current offset.
   */
  setPageSize: (pageSize: number) => void;
  /**
   * Set the search term. Resets the page back to 1 so results aren't shown
   * from a stale offset (a recurring list-screen bug).
   */
  setSearch: (search: string) => void;
  /** Reset the page back to 1. Call this when any external filter changes. */
  resetPage: () => void;
}

/**
 * Dependency-free pagination + search state for list screens. The key
 * behaviour is that changing the search term (or page size) resets the page
 * to 1, which is the bug most carrier list screens repeated by hand.
 *
 * Filter controls that live outside this hook (e.g. a status `<select>`)
 * should call `resetPage()` in their own onChange to get the same guarantee.
 */
export function usePagedState(options: UsePagedStateOptions = {}): PagedState {
  const { initialPage = 1, initialPageSize = 20, initialSearch = "" } = options;

  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [search, setSearchState] = useState(initialSearch);

  const resetPage = useCallback(() => setPage(1), []);

  const setSearch = useCallback((next: string) => {
    setSearchState(next);
    setPage(1);
  }, []);

  const setPageSize = useCallback((next: number) => {
    setPageSizeState(next);
    setPage(1);
  }, []);

  return {
    page,
    pageSize,
    search,
    setPage,
    setPageSize,
    setSearch,
    resetPage,
  };
}
